import type { ReactNode } from "react";
import { Link2 } from "lucide-react";

import type { OpsDocBlock, OpsDocPage } from "../../shared/operations-docs-schemas";
import { DocumentBody } from "../knowledge/DocumentBody";
import { Badge } from "../components/ui/badge";
type DocsListBlock = Extract<
  OpsDocBlock,
  { type: "bulleted-list" | "numbered-list" | "checklist" }
>;
function isDocsListBlock(block: OpsDocBlock): block is DocsListBlock {
  return (
    block.type === "bulleted-list" || block.type === "numbered-list" || block.type === "checklist"
  );
}

function renderTextBlock(block: Exclude<OpsDocBlock, DocsListBlock>, pageId: string) {
  if (block.type === "markdown") return <DocumentBody markdown={block.text} pageId={pageId} />;
  if (block.type === "divider") return <hr className="my-7 border-border" />;
  if (block.type === "heading-one")
    return <h2 className="mt-8 text-2xl font-bold text-balance">{block.text}</h2>;
  if (block.type === "heading-two")
    return <h3 className="mt-6 text-lg font-bold text-balance">{block.text}</h3>;
  if (block.type === "quote")
    return (
      <blockquote className="border-l-2 border-primary pl-4 italic text-muted-foreground">
        {block.text}
      </blockquote>
    );
  if (block.type === "code")
    return (
      <pre className="overflow-x-auto rounded-sm bg-muted p-4 font-mono text-sm">
        <code>{block.text}</code>
      </pre>
    );
  return <p className="whitespace-pre-wrap leading-7">{block.text}</p>;
}

function ReadList({ blocks }: { blocks: DocsListBlock[] }) {
  const type = blocks[0]?.type;
  if (type === "numbered-list") {
    return (
      <ol className="list-decimal space-y-2 pl-6 marker:text-muted-foreground">
        {blocks.map((block) => (
          <li key={block.id}>{block.text}</li>
        ))}
      </ol>
    );
  }
  if (type === "checklist") {
    return (
      <ul aria-label="Checklist" className="list-none space-y-2">
        {blocks.map((block) => (
          <li className="flex items-start gap-2" key={block.id}>
            <span aria-hidden="true">
              {block.type === "checklist" && block.checked ? "☑" : "☐"}
            </span>
            <span>{block.text}</span>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <ul className="list-disc space-y-2 pl-6 marker:text-muted-foreground">
      {blocks.map((block) => (
        <li key={block.id}>{block.text}</li>
      ))}
    </ul>
  );
}

function ReadBlocks({ blocks, pageId }: { blocks: OpsDocBlock[]; pageId: string }) {
  const content = [];
  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index];
    if (!block) continue;
    if (!isDocsListBlock(block)) {
      content.push(<div key={block.id}>{renderTextBlock(block, pageId)}</div>);
      continue;
    }
    const group = [block];
    while (blocks[index + 1]?.type === block.type) {
      const next = blocks[index + 1];
      if (!next || !isDocsListBlock(next)) break;
      group.push(next);
      index += 1;
    }
    content.push(<ReadList blocks={group} key={block.id} />);
  }
  return content;
}

export function DocsReadPage({ page, details }: { page: OpsDocPage; details?: ReactNode }) {
  const empty = page.blocks.every(
    (block) => block.type === "divider" || !("text" in block) || block.text.trim() === "",
  );
  return (
    <article className="mx-auto w-full max-w-[860px] px-5 py-10 sm:px-12 lg:py-14">
      <header className="mb-7">
        {details}
        <h1 className="text-4xl leading-tight font-semibold tracking-tight text-balance">
          {page.title}
        </h1>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {!details && <span>Owned by {page.ownerDisplayName} ·</span>}
          <span>Revision {page.revision}</span>
        </div>
      </header>
      <div className="space-y-4 text-base text-pretty">
        {empty ? (
          <p className="text-muted-foreground">This page is empty.</p>
        ) : (
          <ReadBlocks blocks={page.blocks} pageId={page.id} />
        )}
      </div>
      {page.links.length > 0 ? (
        <footer className="mt-10 border-t pt-5">
          <h2 className="mb-2 text-xs font-semibold text-muted-foreground">Linked records</h2>
          <div className="flex flex-wrap gap-2">
            {page.links.map((link) => (
              <Badge key={`${link.entityType}:${link.entityId}`} variant="outline">
                <Link2 className="size-3" /> {link.label}
              </Badge>
            ))}
          </div>
        </footer>
      ) : null}
    </article>
  );
}
